import { nodeAdvertisesDurableInbox } from "../inbox/durableMode.js";
import { runtimeEnvFlag } from "./runtimeEnvFlag.js";

/**
 * Single owner of `sdk.subscriptions.onMailboxDeposited(...)` on the
 * chat-server. Feeds each live push frame `{ mailboxId, eventId,
 * ciphertextB64 }` into the serialized InboundDepositPipeline — the SAME path
 * the InboxCatchupService uses for missed deposits. Both sources share one
 * ordered pipeline so a burst (e.g. member.join immediately followed by a
 * message) is applied in order, never raced (it used to be a fire-and-forget
 * `bus.emit`, which let a message be dropped ahead of its membership op).
 *
 * Ack-on-success: once a pushed deposit is decrypted (or is a dedup hit), the
 * buffer copy is acked (`sdk.mailbox.ack`) so the relay buffer drains in steady
 * state instead of relying on the catch-up drain. A failed decrypt is NOT acked
 * — it is left for the catch-up drain to retry/quarantine (D1).
 *
 * Wired in by ServerRuntimeService.connect() after the inbox claim registers
 * with the node.
 *
 * Lifecycle: attach() returns an off-handle the caller invokes on
 * disconnect/stop to detach the SDK subscription.
 */
export class MailboxPushBridge {
  static attach({ sdk, bus, logger = console } = {}) {
    if (!sdk || !sdk.subscriptions || typeof sdk.subscriptions.onMailboxDeposited !== "function") {
      throw new Error("MailboxPushBridge.attach requires sdk.subscriptions.onMailboxDeposited");
    }
    if (!sdk.mailbox || typeof sdk.mailbox.ack !== "function") {
      throw new Error("MailboxPushBridge.attach requires sdk.mailbox.ack");
    }
    if (!bus || typeof bus.emit !== "function") {
      throw new Error("MailboxPushBridge.attach requires bus");
    }
    const pipeline = bus.services && bus.services.inboundPipeline;
    if (!pipeline || typeof pipeline.submit !== "function") {
      throw new Error("MailboxPushBridge.attach requires bus.services.inboundPipeline");
    }
    // Durable mode (S2): the catch-up service owns cursor advancement. The push
    // bridge never cursorAcks directly — on a consumed push it nudges the (coalesced)
    // drain, which re-lists the just-consumed seqs as dedup-hits and advances the
    // server-held cursor. Legacy mode is unchanged (ack-delete the buffer copy).
    const catchup = bus.services && bus.services.inboxCatchup ? bus.services.inboxCatchup : null;
    const off = sdk.subscriptions.onMailboxDeposited((frame) => {
      // Enqueue into the ORDERED queue, then ack the buffer copy once the deposit
      // resolves successfully. submit() logs + swallows handler errors internally
      // and resolves with a status object, so the sync guard only covers enqueue.
      let run;
      try {
        run = pipeline.submit(frame);
      } catch (err) {
        const msg = err && err.message ? err.message : String(err);
        logger.error("[MailboxPushBridge] enqueue to inbound pipeline failed: " + msg);
        return;
      }
      Promise.resolve(run)
        .then((result) => {
          // Ack-safe ONLY when the deposit is DURABLE (audit P1.1): a decrypted
          // user message must be durably staged-or-applied before the legacy
          // ack-DELETE removes its buffer copy. A bare decrypt that then fails to
          // stage AND apply is NOT acked — the catch-up drain retries it. `durable`
          // falls back to `consumed` for non-durable pipelines/mocks that predate it.
          const durable = result && result.durable != null ? result.durable : (result && result.consumed);
          const ok = Boolean(durable || (result && result.alreadyProcessed));
          const ids = MailboxPushBridge.#frameIds(frame);
          if (runtimeEnvFlag("REZ_PEERLINK_TRACE")) {
            logger.log(
              "[PLTRACE] pushBridge evt=" + ids.eventId
              + " consumed=" + (result && result.consumed ? 1 : 0)
              + " decryptOk=" + (result && result.decryptOk ? 1 : 0)
              + " already=" + (result && result.alreadyProcessed ? 1 : 0)
              + " reason=" + (result && result.reason ? result.reason : "-")
              + " -> " + (ok ? "ACK" : "LEAVE"),
            );
          }
          if (!ok) return null;
          if (nodeAdvertisesDurableInbox(sdk)) {
            // Durable node: NEVER ack-delete (the home log is the system of record
            // and the device cursor is the only mutation). The deposit is already
            // decrypted/applied + seq-dedup-marked by the pipeline; trigger the
            // coalesced drain to advance the cursor (it re-lists the just-consumed
            // seqs as dedup-hits and cursorAcks them — no re-decrypt, no list spam).
            if (catchup && typeof catchup.requestDrain === "function") {
              catchup.requestDrain().catch((err) => {
                const m = err && err.message ? err.message : String(err);
                logger.error("[MailboxPushBridge] durable cursor-drain trigger failed: " + m);
              });
            }
            return null;
          }
          if (!ids.mailboxId || !ids.eventId) return null;
          return sdk.mailbox.ack({ mailboxId: ids.mailboxId, eventId: ids.eventId });
        })
        .catch((err) => {
          const msg = err && err.message ? err.message : String(err);
          logger.error("[MailboxPushBridge] post-submit ack failed: " + msg);
        });
    });
    return typeof off === "function" ? off : () => {};
  }

  static #frameIds(frame) {
    const body = frame && frame.body && typeof frame.body === "object" ? frame.body : (frame || {});
    const mailboxId = typeof body.mailboxId === "string" ? body.mailboxId : "";
    const eventId = typeof body.eventId === "string" ? body.eventId : "";
    return { mailboxId, eventId };
  }
}
